// Reading GET /assignments/{assignmentId}/submissions/status for EXTRACTION, the
// way feedbackRun.js reads the same response for marking.
//
// All the derivation, none of the I/O — the same split feedbackRun.js and
// submissionBatch.js keep, so the Converting screen, the Review chips and the
// save at Continue all read one response through one set of rules.
//
// The kick itself is one call now (POST /assignments/{id}/submissions/extractText).
// The browser used to fan it out per student, which is why this file's ancestor
// lived in draftSubmissions.js and had to know about attachment generations and
// 409 retries. None of that is the client's business any more.

import { isRichTextEmpty } from '../../Stimulus/stimulusHelpers';
import { submissionIdOf } from './feedbackRun';

/**
 * The server's vocabulary is three words — EXTRACTING, EXTRACTED, FAILED — and
 * the field is NULLABLE. A row reports no status at all when the run had nothing
 * to do for it: a typed response carries no files to read, and an answer that was
 * already converted is not claimed again.
 *
 * PENDING and SKIPPED are ours, not the server's. They are kept because a page
 * can still carry them from an older draft, and because naming the states the
 * poll waits on is clearer than a set with a hole in it.
 */
export const EXTRACTION_STATUS = {
  PENDING: 'PENDING',
  EXTRACTING: 'EXTRACTING',
  EXTRACTED: 'EXTRACTED',
  FAILED: 'FAILED',
  SKIPPED: 'SKIPPED',
};

// Finished, whichever way it went. A FAILED row is a reported outcome, not a row
// still owed something: the teacher can type the text in at Review.
const SETTLED = new Set([
  EXTRACTION_STATUS.EXTRACTED,
  EXTRACTION_STATUS.FAILED,
  EXTRACTION_STATUS.SKIPPED,
]);

// The only two a poll can still change.
const IN_FLIGHT = new Set([
  EXTRACTION_STATUS.PENDING,
  EXTRACTION_STATUS.EXTRACTING,
]);

const rowsOf = (status) =>
  Array.isArray(status?.submissions) ? status.submissions : [];

/**
 * A response the run SKIPPED reports its text and no status at all.
 *
 * POST .../extractText answers {requested: 1, queued: 0, skipped: 1} for a typed
 * response — there are no files to read — and the row then arrives from
 * /submissions/status as {extraction: {text: "<p>…</p>"}}, with no `status` key.
 *
 * That row is finished: the text is right there and no poll will ever add to it.
 * Reading it as statusless is the deadlock this closes — the student never joins
 * settledStudentIds, so Review holds them on "Converting…", while nothing is in
 * flight, so isExtractionActive switches the poll OFF and nothing ever arrives to
 * change it. Text with no status is converted text.
 *
 * Only text rescues a statusless row. One with nothing in it is still unknown,
 * and unknown must keep the editor locked rather than invite a teacher to type
 * into an answer that extraction may still land on.
 */
const skippedStatusOf = (extraction) =>
  isRichTextEmpty(extraction?.text ?? '') ? null : EXTRACTION_STATUS.EXTRACTED;

/**
 * The mirror of the rule above: a status of EXTRACTED with nothing in it.
 *
 * Observed live as {status: "EXTRACTED", text: "<p></p>", failureReason: null}.
 * Taken at its word that row is a success, so Review gave the student an unlocked
 * empty editor and no chip at all — the same picture as a student still
 * converting, and as one whose page really was blank. The teacher had no way to
 * tell which, and nothing to act on.
 *
 * Extraction that produced no text did not read the response, whatever it called
 * itself. Saying so costs nothing the teacher wanted: FAILED is settled too, so
 * they still type the text in and still continue — but they are told, the chip
 * names the problem, and a retry picks the row back up.
 */
const isEmptyRead = (extraction) =>
  extraction?.status === EXTRACTION_STATUS.EXTRACTED &&
  isRichTextEmpty(extraction?.text ?? '');

/** The status a row reports, with both no-text gaps closed. */
export const statusOfExtraction = (extraction) => {
  if (isEmptyRead(extraction)) return EXTRACTION_STATUS.FAILED;
  return extraction?.status || skippedStatusOf(extraction);
};

export const extractionStatusOf = (row) => statusOfExtraction(row?.extraction);

export const isRowSettled = (row) => SETTLED.has(extractionStatusOf(row));

export const isRowExtracted = (row) =>
  extractionStatusOf(row) === EXTRACTION_STATUS.EXTRACTED;

const isRowInFlight = (row) => IN_FLIGHT.has(extractionStatusOf(row));

/** Something is still moving, so another read can tell us something new. */
export const isExtractionActive = (status) => rowsOf(status).some(isRowInFlight);

/**
 * Nothing is owed any more — which is NOT the same as "every row extracted".
 *
 * A row the run skipped reports no status and never will, so a rule of "every row
 * is in SETTLED" would wait forever on a batch of typed responses. The question
 * the wizard actually needs answered is whether another read could still tell it
 * something, and only an in-flight row can.
 *
 * The caller has to know a read has landed since the kick before trusting this —
 * before the kick, every row is quiet for the opposite reason.
 */
export const isExtractionSettledRun = (status) => !isExtractionActive(status);

/**
 * The student the wizard can move on for. Server order is the batch's routing
 * order, so this is the earliest-routed student who is actually readable — not
 * whichever row happened to come back first.
 */
export const firstExtractedRow = (status) =>
  rowsOf(status).find(isRowExtracted) || null;

const studentIdOf = (row) =>
  row?.studentId == null ? null : String(row.studentId);

/**
 * Which students' extraction has reported — the gate on the save at Continue.
 *
 * Strings, because the wizard keys by its own studentIds and the roster is the
 * server's: the two sides of this lookup only ever meet as strings, the same rule
 * reviewEdits.js keeps.
 */
export const settledStudentIds = (status) =>
  new Set(rowsOf(status).filter(isRowSettled).map(studentIdOf).filter(Boolean));

export const extractedCount = (status) => rowsOf(status).filter(isRowExtracted).length;

export const stillConvertingCount = (status) =>
  rowsOf(status).filter(isRowInFlight).length;

const convertingClause = (converting) =>
  converting === 0 ? null : `${converting} still converting`;

/** "12 of 30 converted · 3 still converting" */
export const extractionSummaryMessage = (status) => {
  const rows = rowsOf(status);
  if (rows.length === 0) return '';
  return [
    `${extractedCount(status)} of ${rows.length} converted`,
    convertingClause(stillConvertingCount(status)),
  ]
    .filter(Boolean)
    .join(' · ');
};

/**
 * Exactly the rows a retry should carry. The endpoint accepts a submissionIds
 * subset, so a partly-failed run goes back for the students it failed on and
 * leaves the converted ones alone.
 */
export const failedExtractionSubmissionIds = (status) =>
  rowsOf(status)
    .filter((row) => extractionStatusOf(row) === EXTRACTION_STATUS.FAILED)
    .map(submissionIdOf)
    .filter(Boolean);
